import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { getApiBase, getAuthHeaders } from '../../utils/api';

const AwardWalletComparisons = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [comparisons, setComparisons] = useState([]);
  const [stats, setStats] = useState(null);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState('');
  const [selectedComparison, setSelectedComparison] = useState(null);

  // Filters
  const [filters, setFilters] = useState({
    document_type: '',
    reviewed: '',
    min_match_score: '',
    max_match_score: '',
    days: 30
  });

  const [pagination, setPagination] = useState({
    limit: 20,
    offset: 0
  });

  useEffect(() => {
    if (user?.role === 'superadmin') {
      loadStats();
      loadComparisons();
    }
  }, [user, filters, pagination]);

  const loadStats = async () => {
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      const response = await axios.get(
        `${apiBase}/admin/awardwallet-parsing/comparisons/stats?days=${filters.days}`,
        { headers }
      );
      setStats(response.data.stats);
    } catch (err) {
      console.error('Error loading stats:', err);
    }
  };

  const loadComparisons = async () => {
    setLoading(true);
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();

      const params = new URLSearchParams();
      params.append('days', filters.days);
      params.append('limit', pagination.limit);
      params.append('offset', pagination.offset);

      if (filters.document_type) params.append('document_type', filters.document_type);
      if (filters.reviewed !== '') params.append('reviewed', filters.reviewed);
      if (filters.min_match_score) params.append('min_match_score', filters.min_match_score);
      if (filters.max_match_score) params.append('max_match_score', filters.max_match_score);

      const response = await axios.get(
        `${apiBase}/admin/awardwallet-parsing/comparisons?${params}`,
        { headers }
      );

      setComparisons(response.data.items || []);
      setTotal(response.data.total || 0);
    } catch (err) {
      console.error('Error loading comparisons:', err);
      setError('Failed to load comparisons: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const markReviewed = async (comparisonId, aiCorrect, notes) => {
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();

      await axios.put(
        `${apiBase}/admin/awardwallet-parsing/comparisons/${comparisonId}/review`,
        { ai_correct: aiCorrect, notes },
        { headers }
      );

      loadComparisons();
      setSelectedComparison(null);
    } catch (err) {
      console.error('Error marking reviewed:', err);
      setError('Failed to mark as reviewed: ' + (err.response?.data?.detail || err.message));
    }
  };

  const getMatchScoreColor = (score) => {
    if (score >= 90) return 'text-green-600 bg-green-100';
    if (score >= 70) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString();
  };

  if (user?.role !== 'superadmin') {
    return (
      <div className="p-6 bg-red-50 text-red-600 rounded-lg">
        Access denied. Superadmin privileges required.
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      <div className="mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Parsing Comparisons</h1>
        <p className="mt-2 text-sm sm:text-base text-gray-600">
          Compare AI parser results with AwardWallet parsing results for evaluation.
        </p>
      </div>

      {error && (
        <div className="mb-6 p-3 sm:p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
          <button onClick={() => setError('')} className="ml-4 text-red-500 hover:text-red-700">&times;</button>
        </div>
      )}

      {/* Stats Cards */}
      {stats && (
        <div className="mb-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-4">
          <div className="p-3 sm:p-4 bg-white border rounded-lg">
            <div className="text-lg sm:text-2xl font-bold text-gray-900">{stats.total_comparisons}</div>
            <div className="text-xs sm:text-sm text-gray-500">Total</div>
          </div>
          <div className="p-3 sm:p-4 bg-white border rounded-lg">
            <div className="text-lg sm:text-2xl font-bold text-green-600">{stats.both_parsed}</div>
            <div className="text-xs sm:text-sm text-gray-500">Both Parsed</div>
          </div>
          <div className="p-3 sm:p-4 bg-white border rounded-lg">
            <div className="text-lg sm:text-2xl font-bold text-blue-600">{stats.ai_only}</div>
            <div className="text-xs sm:text-sm text-gray-500">AI Only</div>
          </div>
          <div className="p-3 sm:p-4 bg-white border rounded-lg">
            <div className="text-lg sm:text-2xl font-bold text-purple-600">{stats.aw_only}</div>
            <div className="text-xs sm:text-sm text-gray-500">AW Only</div>
          </div>
          <div className="p-3 sm:p-4 bg-white border rounded-lg col-span-2 sm:col-span-1">
            <div className="text-lg sm:text-2xl font-bold text-gray-900">
              {stats.average_match_score?.toFixed(1)}%
            </div>
            <div className="text-xs sm:text-sm text-gray-500">Avg Match</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="mb-6 p-3 sm:p-4 bg-gray-50 border rounded-lg">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-4">
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Type</label>
            <select
              value={filters.document_type}
              onChange={(e) => setFilters(prev => ({ ...prev, document_type: e.target.value }))}
              className="w-full px-2 sm:px-3 py-2 border rounded-lg text-xs sm:text-sm"
            >
              <option value="">All Types</option>
              <option value="flight">Flight</option>
              <option value="hotel">Hotel</option>
              <option value="car_rental">Car Rental</option>
              <option value="rail">Rail</option>
              <option value="cruise">Cruise</option>
            </select>
          </div>
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={filters.reviewed}
              onChange={(e) => setFilters(prev => ({ ...prev, reviewed: e.target.value }))}
              className="w-full px-2 sm:px-3 py-2 border rounded-lg text-xs sm:text-sm"
            >
              <option value="">All</option>
              <option value="false">Unreviewed</option>
              <option value="true">Reviewed</option>
            </select>
          </div>
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Min %</label>
            <input
              type="number"
              value={filters.min_match_score}
              onChange={(e) => setFilters(prev => ({ ...prev, min_match_score: e.target.value }))}
              placeholder="0"
              className="w-full px-2 sm:px-3 py-2 border rounded-lg text-xs sm:text-sm"
              min="0"
              max="100"
            />
          </div>
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Max %</label>
            <input
              type="number"
              value={filters.max_match_score}
              onChange={(e) => setFilters(prev => ({ ...prev, max_match_score: e.target.value }))}
              placeholder="100"
              className="w-full px-2 sm:px-3 py-2 border rounded-lg text-xs sm:text-sm"
              min="0"
              max="100"
            />
          </div>
          <div className="col-span-2 sm:col-span-1">
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Period</label>
            <select
              value={filters.days}
              onChange={(e) => setFilters(prev => ({ ...prev, days: parseInt(e.target.value) }))}
              className="w-full px-2 sm:px-3 py-2 border rounded-lg text-xs sm:text-sm"
            >
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
              <option value="365">Last year</option>
            </select>
          </div>
        </div>
      </div>

      {/* Comparisons List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600 text-sm">Loading comparisons...</span>
        </div>
      ) : comparisons.length === 0 ? (
        <div className="text-center py-12 text-gray-500 text-sm">
          No comparisons found for the selected filters.
        </div>
      ) : (
        <div className="bg-white border rounded-lg overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden md:table-cell">Subject</th>
                <th className="px-3 sm:px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">AI</th>
                <th className="px-3 sm:px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">AW</th>
                <th className="px-3 sm:px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Match</th>
                <th className="px-3 sm:px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase hidden sm:table-cell">Status</th>
                <th className="px-3 sm:px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {comparisons.map((comp) => (
                <tr key={comp.id} className="hover:bg-gray-50">
                  <td className="px-3 sm:px-4 py-3 text-xs sm:text-sm text-gray-600 whitespace-nowrap">
                    {new Date(comp.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-3 sm:px-4 py-3 text-sm">
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                      {comp.document_type || 'unknown'}
                    </span>
                  </td>
                  <td className="px-3 sm:px-4 py-3 text-sm text-gray-900 max-w-[150px] truncate hidden md:table-cell">
                    {comp.email_metadata?.subject || '-'}
                  </td>
                  <td className="px-3 sm:px-4 py-3 text-center">
                    <span className={`text-xs sm:text-sm font-medium ${comp.ai_result?.success ? 'text-green-600' : 'text-red-600'}`}>
                      {comp.ai_result?.confirmations_count || 0}
                    </span>
                  </td>
                  <td className="px-3 sm:px-4 py-3 text-center">
                    <span className={`text-xs sm:text-sm font-medium ${comp.awardwallet_result?.success ? 'text-green-600' : 'text-red-600'}`}>
                      {comp.awardwallet_result?.confirmations_count || 0}
                    </span>
                  </td>
                  <td className="px-3 sm:px-4 py-3 text-center">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getMatchScoreColor(comp.comparison_metrics?.match_score || 0)}`}>
                      {(comp.comparison_metrics?.match_score || 0).toFixed(0)}%
                    </span>
                  </td>
                  <td className="px-3 sm:px-4 py-3 text-center hidden sm:table-cell">
                    {comp.reviewed ? (
                      <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs">
                        Reviewed
                      </span>
                    ) : (
                      <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">
                        Pending
                      </span>
                    )}
                  </td>
                  <td className="px-3 sm:px-4 py-3 text-right">
                    <button
                      onClick={() => setSelectedComparison(comp)}
                      className="text-blue-600 hover:text-blue-800 text-xs sm:text-sm whitespace-nowrap"
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {total > pagination.limit && (
        <div className="mt-4 flex flex-col sm:flex-row justify-between items-center gap-2">
          <div className="text-xs sm:text-sm text-gray-600">
            Showing {pagination.offset + 1} - {Math.min(pagination.offset + pagination.limit, total)} of {total}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setPagination(prev => ({ ...prev, offset: Math.max(0, prev.offset - prev.limit) }))}
              disabled={pagination.offset === 0}
              className="px-3 sm:px-4 py-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50 text-xs sm:text-sm"
            >
              Previous
            </button>
            <button
              onClick={() => setPagination(prev => ({ ...prev, offset: prev.offset + prev.limit }))}
              disabled={pagination.offset + pagination.limit >= total}
              className="px-3 sm:px-4 py-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50 text-xs sm:text-sm"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {selectedComparison && (
        <ComparisonDetailModal
          comparison={selectedComparison}
          onClose={() => setSelectedComparison(null)}
          onMarkReviewed={markReviewed}
        />
      )}
    </div>
  );
};

const ComparisonDetailModal = ({ comparison, onClose, onMarkReviewed }) => {
  const [activeTab, setActiveTab] = useState('comparison');
  const [notes, setNotes] = useState('');

  const metrics = comparison.comparison_metrics || {};
  const aiData = comparison.ai_result?.data || {};
  const awData = comparison.awardwallet_result?.data || {};

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="px-4 sm:px-6 py-4 border-b flex justify-between items-center">
          <h2 className="text-lg sm:text-xl font-semibold">Comparison Details</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl p-1">&times;</button>
        </div>

        {/* Tabs */}
        <div className="border-b overflow-x-auto">
          <div className="flex min-w-max">
            {['comparison', 'ai_data', 'aw_data', 'metrics'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-3 sm:px-4 py-3 text-xs sm:text-sm font-medium border-b-2 whitespace-nowrap ${
                  activeTab === tab
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab === 'comparison' && 'Comparison'}
                {tab === 'ai_data' && 'AI Result'}
                {tab === 'aw_data' && 'AW Result'}
                {tab === 'metrics' && 'Metrics'}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 200px)' }}>
          {activeTab === 'comparison' && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="p-3 sm:p-4 bg-gray-50 rounded-lg">
                  <h3 className="font-medium mb-2 text-sm sm:text-base">Email Info</h3>
                  <div className="text-xs sm:text-sm space-y-1">
                    <div className="truncate"><span className="text-gray-500">From:</span> {comparison.email_metadata?.from}</div>
                    <div className="truncate"><span className="text-gray-500">Subject:</span> {comparison.email_metadata?.subject}</div>
                    <div><span className="text-gray-500">Type:</span> {comparison.document_type}</div>
                  </div>
                </div>
                <div className="p-3 sm:p-4 bg-gray-50 rounded-lg">
                  <h3 className="font-medium mb-2 text-sm sm:text-base">Results Summary</h3>
                  <div className="text-xs sm:text-sm space-y-1">
                    <div><span className="text-gray-500">AI Confirmations:</span> {comparison.ai_result?.confirmations_count || 0}</div>
                    <div><span className="text-gray-500">AW Confirmations:</span> {comparison.awardwallet_result?.confirmations_count || 0}</div>
                    <div><span className="text-gray-500">Match Score:</span> {(metrics.match_score || 0).toFixed(1)}%</div>
                  </div>
                </div>
              </div>

              {/* Field Comparisons */}
              {Object.keys(metrics.field_matches || {}).length > 0 && (
                <div className="mt-4">
                  <h3 className="font-medium mb-2">Field Comparison</h3>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="px-3 py-2 text-left">Field</th>
                        <th className="px-3 py-2 text-left">AI Value</th>
                        <th className="px-3 py-2 text-left">AW Value</th>
                        <th className="px-3 py-2 text-center">Match</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(metrics.field_matches).map(([field, data]) => (
                        <tr key={field} className="border-b">
                          <td className="px-3 py-2 font-medium">{field}</td>
                          <td className="px-3 py-2">{data.ai || '-'}</td>
                          <td className="px-3 py-2">{data.aw || '-'}</td>
                          <td className="px-3 py-2 text-center">
                            {data.matches ? (
                              <span className="text-green-600">&#10003;</span>
                            ) : (
                              <span className="text-red-600">&#10007;</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === 'ai_data' && (
            <pre className="bg-gray-50 p-4 rounded-lg overflow-x-auto text-sm">
              {JSON.stringify(aiData, null, 2)}
            </pre>
          )}

          {activeTab === 'aw_data' && (
            <pre className="bg-gray-50 p-4 rounded-lg overflow-x-auto text-sm">
              {JSON.stringify(awData, null, 2)}
            </pre>
          )}

          {activeTab === 'metrics' && (
            <pre className="bg-gray-50 p-4 rounded-lg overflow-x-auto text-sm">
              {JSON.stringify(metrics, null, 2)}
            </pre>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 sm:px-6 py-4 border-t bg-gray-50">
          {!comparison.reviewed ? (
            <div className="space-y-3">
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add review notes (optional)..."
                className="w-full px-3 py-2 border rounded-lg text-sm"
                rows={2}
              />
              <div className="flex flex-wrap justify-end gap-2 sm:gap-3">
                <button
                  onClick={() => onMarkReviewed(comparison.id, true, notes)}
                  className="px-3 sm:px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-xs sm:text-sm"
                >
                  AI Correct
                </button>
                <button
                  onClick={() => onMarkReviewed(comparison.id, false, notes)}
                  className="px-3 sm:px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-xs sm:text-sm"
                >
                  AI Incorrect
                </button>
                <button
                  onClick={onClose}
                  className="px-3 sm:px-4 py-2 border rounded-lg hover:bg-gray-100 text-xs sm:text-sm"
                >
                  Close
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
              <div className="text-xs sm:text-sm text-gray-600">
                Reviewed by {comparison.reviewed_by_user_email} on {new Date(comparison.reviewed_at).toLocaleDateString()}
                {comparison.ai_correct !== undefined && (
                  <span className={`ml-2 ${comparison.ai_correct ? 'text-green-600' : 'text-red-600'}`}>
                    ({comparison.ai_correct ? 'AI Correct' : 'AI Incorrect'})
                  </span>
                )}
              </div>
              <button
                onClick={onClose}
                className="px-3 sm:px-4 py-2 border rounded-lg hover:bg-gray-100 text-xs sm:text-sm"
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AwardWalletComparisons;
